import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const isLoginPage = () => {
  return window.location.pathname.includes('/login') || window.location.pathname.includes('/internal/login');
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  if (!isLoginPage()) {
    window.location.href = '/login';
  }
};

const isAuthEndpoint = (url) => {
  return url?.includes('/api/auth/refresh') || url?.includes('/api/auth/login');
};

const enqueueFailedRequest = (originalRequest) => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  }).then(token => {
    originalRequest.headers.Authorization = `Bearer ${token}`;
    return api(originalRequest);
  });
};

const refreshTokens = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }
  const response = await axios.post(`${API_BASE}/api/auth/refresh`, {
    refresh_token: refreshToken
  });
  return response.data;
};

const storeNewTokens = (accessToken, newRefreshToken) => {
  localStorage.setItem('token', accessToken);
  localStorage.setItem('refreshToken', newRefreshToken);
  api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
};

const attemptTokenRefresh = async (originalRequest, error) => {
  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const data = await refreshTokens();
    if (!data) {
      isRefreshing = false;
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    const { access_token, refresh_token: newRefreshToken } = data;
    storeNewTokens(access_token, newRefreshToken);
    originalRequest.headers.Authorization = `Bearer ${access_token}`;
    processQueue(null, access_token);
    isRefreshing = false;
    return api(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError, null);
    isRefreshing = false;
    clearAuthAndRedirect();
    return Promise.reject(refreshError);
  }
};

const shouldReject = (error) => {
  return error.response?.status !== 401 || error.config._retry;
};

// Handle 401 responses with token refresh
const handle401 = async (error) => {
  if (shouldReject(error)) {
    return Promise.reject(error);
  }

  const originalRequest = error.config;

  if (isAuthEndpoint(originalRequest.url)) {
    clearAuthAndRedirect();
    return Promise.reject(error);
  }

  if (isRefreshing) {
    return enqueueFailedRequest(originalRequest);
  }

  return attemptTokenRefresh(originalRequest, error);
};

api.interceptors.response.use((r) => r, handle401);

export default api;
